// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Box,
  Button,
  Container as MuiContainer,
  GlobalStyles,
  lighten,
  Paper,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useMemo } from 'react';
import Countdown from 'react-countdown';

import { isRelease } from '../../config';
import { usePhase } from '../../hooks/usePhase';
import ButtonVerify from './ButtonVerify';
import IconSuccess from './icon_success.svg';

const Wrapper = styled(Box)({
  paddingTop: 128,
  paddingBottom: 128
});

const Container = styled(MuiContainer)({
  margin: '0 auto'
});

const Content = styled(Paper)({
  marginTop: 24,
  padding: '20px 44px',
  background: 'rgba(255,255,255,0.8)',
  boxShadow: '0px 3px 30px rgba(27,145,150,0.3)',
  borderRadius: 8
});

function Verify() {
  const phases = usePhase();
  const endTime = useMemo(() => (isRelease ? 1671235200000 : 1676962917159), []);
  const end = useMemo(() => {
    return Date.now() > endTime;
  }, [endTime]);
  const prizeList = useMemo(
    () =>
      '广东吴彦祖#4033 Adiating#0843 Aaliyah#1197 dk1dk9#6673 haierhao#6428 Damiano#2169 我叫大熊#2630 Stragos#5466 jesonli#5904 AliceAha#5719 afjk#0852 laowan#9333 giatule2#1175 phyllis258#0112 加密舟哥#7071 abram0101#4013 xyzaql#6195 meiguo888808#8846 Yuage#8931 Goobu#4726'.split(
        ' '
      ),
    []
  );

  return (
    <Wrapper>
      <GlobalStyles
        styles={{
          body: {
            background: 'url(png_bac.svg) no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }
        }}
      />
      <Container>
        <Typography fontWeight={500} mb={2.5} textAlign="center" variant="h2">
          Is the team you guessed in the semi-finals?
        </Typography>
        <Typography textAlign="center">
          Zero-Knowledge Proof: Prove your team enters the semi-finals without disclosing its name
          to anyone.
        </Typography>
        <Box
          component={Content}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h5">
              20 raffle winners for $1000 will be announced on Dec. 17
            </Typography>
            <Button
              endIcon={<ArrowRightAltIcon />}
              onClick={() =>
                window.open(
                  'https://zcloaknetwork.medium.com/phase-ii-worlds-first-zk-guessing-for-the-world-cup-games-zero-knowledge-proof-for-semi-finals-680d66404639'
                )
              }
              sx={{
                marginTop: 2,
                background: '#000000',
                border: '1px solid #1B9196',
                boxShadow: '0px 3px 20px rgba(27,145,150,0.71)',
                borderRadius: '9px',
                color: '#fff',
                ':hover': {
                  background: '#000000'
                }
              }}
              variant="contained"
            >
              Phase Guide
            </Button>
          </Box>
          <Box>
            <Typography mb={2}>End Countdown: </Typography>
            <Countdown
              date={endTime}
              renderer={({ completed, days, hours, minutes, seconds }) => {
                if (completed) {
                  return 'End';
                } else {
                  return (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        '>div': {
                          width: '48px',
                          height: '48px',
                          background: '#333333',
                          border: '1px solid #707070',
                          borderRadius: '4px',
                          fontSize: '34px',
                          lineHeight: '48px',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#E2B436'
                        }
                      }}
                    >
                      <Box>{days}D</Box>
                      <Box>{String(hours).length === 1 ? `0${hours}` : hours}</Box>
                      <Box>{String(minutes).length === 1 ? `0${minutes}` : minutes}</Box>
                      <Box>{String(seconds).length === 1 ? `0${seconds}` : seconds}</Box>
                    </Stack>
                  );
                }
              }}
            />
          </Box>
        </Box>
        {end ? (
          <Content>
            <>
              <Typography mb={4} variant="h6">
                Congratulations to the following 20 lucky draw winners who will share our $1000
                prize pool! Please refer to the Phase IV Guide after Dec.20 for details on how to
                claim your prizes.
                <br />
                <ul
                  style={{
                    display: 'flex',
                    width: '100%',
                    flexFlow: 'row wrap',
                    alignContent: 'flex-start'
                  }}
                >
                  {prizeList.map((name, idx) => {
                    return (
                      <li key={idx} style={{ minWidth: '220px' }}>
                        {name}
                      </li>
                    );
                  })}
                </ul>
              </Typography>
            </>

            <Box textAlign="center">
              <Button disabled size="large" variant="contained">
                Phase II is over
              </Button>
            </Box>
          </Content>
        ) : phases?.[1] ? (
          <Content
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 276,
              marginTop: 3,
              background: `linear-gradient(330deg, ${lighten('#FFEDB7', 0.6)} 0%, ${lighten(
                '#FFA6CB',
                0.6
              )} 100%)`,
              '>img': {
                marginBottom: 3
              }
            }}
          >
            <img src={IconSuccess} />
            Congratulations！The team you guessed is in the semi-finals.
          </Content>
        ) : (
          <Content>
            <Typography mb={4} variant="h6">
              During the verification process, zero-knowledge proof operations are run on your
              computer only through your wallet, and the final proof of the calculation will be sent
              to our server. Our server only knows if your guess reached the semi-finals, but it
              doesn’t know exactly which team you picked.
            </Typography>
            <Box textAlign="center">
              <ButtonVerify phases={phases} />
            </Box>
          </Content>
        )}
      </Container>
    </Wrapper>
  );
}

export default Verify;
